import React from "react";
import {
	Button,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	RequiredKeys,
	Row,
} from "@hex-insights/core";
import { EmptyView } from "../../../../Components/Empty";
import { Icon, IconContainer } from "../../../../Components/Icon";
import { AddressDetailFragment, Person, PersonFilterInput, usePersonNamesQuery } from "../../../../Utilities";
import { AddressForm } from "../../../Forms";
import { AddressCard } from "../Card";
import { AddressCreatePermissionRequired } from "../PermissionRequired";
import styles from "./styles.module.css";

export type AddressListModalProps = {
	addresses: Omit<AddressDetailFragment, "people">[];
	initialCreatePersonIDs?: Person["id"][];
	initialCreatePeopleFilter?: PersonFilterInput;
	createFormNameSuffix?: string;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function AddressListModal({
	addresses,
	initialCreatePersonIDs,
	initialCreatePeopleFilter,
	createFormNameSuffix,
	ifRef,
	onClose,
	className,
	style,
}: AddressListModalProps) {
	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<IconContainer color="var(--theme-colors__blue--light)">
						<Icon.House color="var(--theme-colors__blue)" size={18} />
					</IconContainer>

					<Heading.H2 noMargin>Addresses</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body style={{ display: "flex", flexDirection: "column" }}>
				<AddressEditableList
					addresses={addresses}
					initialCreatePersonIDs={initialCreatePersonIDs}
					initialCreatePeopleFilter={initialCreatePeopleFilter}
					createFormNameSuffix={createFormNameSuffix}
				/>
			</Modal.Body>
		</Modal>
	);
}

export type AddressEditableListProps = {
	addresses: Omit<AddressDetailFragment, "people">[];
	initialCreatePersonIDs?: Person["id"][];
	initialCreatePeopleFilter?: PersonFilterInput;
	createFormNameSuffix?: string;
	hidePeople?: boolean;
};

export function AddressEditableList({
	addresses,
	initialCreatePersonIDs,
	initialCreatePeopleFilter,
	createFormNameSuffix,
	hidePeople,
}: AddressEditableListProps) {
	const {
		isModalOpen: isCreateModalOpen,
		openModal: openCreateModal,
		closeModal: closeCreateModal,
	} = Modal.useToggle();

	return (
		<React.Fragment>
			<Conditional>
				<If condition={addresses.length === 0}>
					<EmptyView
						IconComponent={Icon.EnvelopeSimple}
						iconSize={54}
						primaryText="Addresses"
						secondaryText="No addresses listed"
					>
						<AddressCreatePermissionRequired>
							<Button variant="link" size="small" onClick={openCreateModal}>
								Add Address
							</Button>
						</AddressCreatePermissionRequired>
					</EmptyView>
				</If>
				<Else>
					<Column justify="spaced-start" verticalSpacing="1.25rem">
						<Column justify="spaced-start" verticalSpacing="1rem">
							{addresses.map((address) => (
								<AddressCard key={address.id} address={address} slim editable hidePeople={hidePeople} />
							))}
						</Column>

						<AddressCreatePermissionRequired>
							<Button variant="link" size="small" onClick={openCreateModal}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									<Icon.Plus size={16} /> Add Address
								</Row>
							</Button>
						</AddressCreatePermissionRequired>
					</Column>
				</Else>
			</Conditional>

			<Modal.If condition={isCreateModalOpen}>
				<CreateModal
					initialPersonIDs={initialCreatePersonIDs}
					initialPeopleFilter={initialCreatePeopleFilter}
					formNameSuffix={createFormNameSuffix}
					hidePeople={hidePeople}
					onClose={closeCreateModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type CreateModalProps = {
	initialPersonIDs?: Person["id"][];
	initialPeopleFilter?: PersonFilterInput;
	formNameSuffix?: string;
	hidePeople?: boolean;
} & RequiredKeys<Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">, "onClose">;

function CreateModal({
	initialPersonIDs,
	initialPeopleFilter,
	formNameSuffix,
	hidePeople,
	ifRef,
	onClose,
	className,
	style,
}: CreateModalProps) {
	const includeInitialPeople = !!initialPersonIDs || !!initialPeopleFilter;
	const loadInitialPeople = !initialPersonIDs && !!initialPeopleFilter;
	const { loading, data } = usePersonNamesQuery({
		variables: { filters: initialPeopleFilter },
		skip: !loadInitialPeople,
	});

	const createFormStateArgs = React.useMemo(
		() => ({
			initialFormValues: {
				personIDs: initialPersonIDs ?? data?.personConnection.edges.map((e) => e.node.id) ?? [],
			},
		}),
		[initialPersonIDs, data],
	);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["create-modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>New Address</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Conditional>
					<If condition={loadInitialPeople && loading && !data}>
						<div style={{ width: "var(--general__field--double---width)" }}>Loading...</div>
					</If>
					<Else>
						<AddressForm.ControlledCreate
							formStateArgs={includeInitialPeople ? createFormStateArgs : undefined}
							formNameSuffix={formNameSuffix}
							onSuccess={onClose}
							hidePeople={hidePeople}
						/>
					</Else>
				</Conditional>
			</Modal.Body>
		</Modal>
	);
}
